import { useParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { PDFViewer } from "@/components/viewers/PDFViewer";
import {
  GenericError,
  PaywallCard,
  ViewerScaffold,
} from "@/components/viewers/ViewerScaffold";
import { useViewerAccess } from "@/components/viewers/ChapterAccessGate";
import { useSignedUrl } from "@/hooks/useSignedUrl";
import { useViewerTracker } from "@/hooks/useViewerTracker";

export function QuestionBankPage() {
  const { chapterId } = useParams<{ chapterId: string }>();
  const { access, paywall, shouldFetch } = useViewerAccess(chapterId, "Question Bank");
  const { reportEndReached } = useViewerTracker({
    chapterId,
    mode: "question-bank",
    active: paywall === null,
    minDwellSeconds: 120,
  });

  // Completion = last page reached AND the dwell floor met. Flicking the
  // scrollbar to the bottom is not finishing a chapter.
  const handlePageChange = (page: number, totalPages: number) => {
    if (totalPages > 0 && page >= totalPages) reportEndReached();
  };

  const { url, isLoading, isError, isLocked, refresh } = useSignedUrl({
    chapterId,
    assetType: "question-bank",
    enabled: shouldFetch,
  });

  const chapter = access.chapter;
  const classId = access.needsClassId ?? chapter?.class_id;

  return (
    <ViewerScaffold
      chapterTitle={chapter?.chapter_name ?? null}
      subjectName={null}
      modeLabel="Question Bank"
      modeIcon={CheckCircle2}
      modeAccent="bg-orange-500/10 text-orange-600 dark:text-orange-400"
      showHeader={false}
    >
      {paywall ?? (
        isLocked ? (
          <PaywallCard modeLabel="Question bank" classId={classId} />
        ) : isError ? (
          <GenericError onRetry={refresh} />
        ) : url ? (
          <PDFViewer
            url={url}
            title="Question Bank"
            subtitle={chapter?.chapter_name ?? null}
            modeIcon={CheckCircle2}
            modeAccent="bg-orange-500/10 text-orange-600 dark:text-orange-400"
            showStudyControls
            onPageChange={handlePageChange}
            className="h-[100dvh] min-h-0"
          />
        ) : isLoading ? (
          <div className="flex-1 grid place-items-center text-sm text-text-muted">
            Preparing the question bank…
          </div>
        ) : null
      )}
    </ViewerScaffold>
  );
}
